import { CountryItem, CountrySelectorService } from '@api'
import {
  attach,
  combine,
  createEffect,
  createEvent,
  createStore,
  sample,
} from 'effector'

import {
  $fanCountryCode,
  $fanId,
  updateFanCountryCode,
} from '@/models/chattingUI/chats/chats.ts'
import { $creatorId } from '@/models/creators.ts'
import { fanInfoService } from '@/modules/chatting/service/fan-info.ts'
import { handleError } from '@/utils/error'

export const filterCountries = (
  countries: CountryItem[],
  isPrimary: boolean,
  regex: RegExp | null,
) => {
  return regex
    ? countries
        .filter(
          ({ name, primary, states }) =>
            primary === isPrimary &&
            (regex.test(name) ||
              states?.some(({ name: stateName }) => regex.test(stateName))),
        )
        .map((country) => ({
          ...country,
          states: country.states?.filter(
            ({ name: stateName, code }) =>
              regex.test(stateName) || code.endsWith('-ZZ'),
          ),
        }))
    : countries.filter(({ primary }) => primary === isPrimary)
}

export const setSearch = createEvent<string>('set fan countries search value')
export const $search = createStore<string>('', {
  name: 'fan countries search value',
}).on(setSearch, (_, value) => value)

export const NO_COUNTRY_SELECTED_LABEL = 'No Country selected'

export const updateCountryFx = attach({
  name: 'update fan country',
  source: {
    creatorId: $creatorId,
    fanId: $fanId,
  },
  mapParams: (country: string | null, { fanId, creatorId }) => ({
    country,
    creatorId: creatorId!,
    fanId: fanId!,
  }),
  effect: createEffect(
    async ({
      creatorId,
      fanId,
      country,
    }: {
      creatorId: number
      fanId: number
      country: string | null
    }) => {
      await fanInfoService.updateCountry(creatorId, fanId, country)

      return { country, fanId }
    },
  ),
})

updateCountryFx.failData.watch((error) => {
  handleError(error, 'Something went wrong while updating country')
})

export const getCountriesList = createEvent('get fan countries list')
export const getCountriesListFx = createEffect({
  name: 'get fan countries list',
  handler: CountrySelectorService.getCountriesWithStates,
})

getCountriesListFx.fail.watch(({ error }) => {
  handleError(
    error,
    'Something went wrong while getting fan countries list',
    getCountriesListFx,
  )
})

export const $fanCountries = createStore<CountryItem[]>([], {
  name: 'fan countries list',
}).on(getCountriesListFx.doneData, (_, data) => data)
export const $topPriorityCountries = combine(
  $search,
  $fanCountries,
  (search, countries) => {
    const regex = search ? new RegExp(search, 'i') : null

    return filterCountries(countries, true, regex)
  },
)
export const $otherCountries = combine(
  $search,
  $fanCountries,
  (search, countries) => {
    const regex = search ? new RegExp(search, 'i') : null

    return filterCountries(countries, false, regex)
  },
)

export const $fanCountryName = combine(
  $fanCountryCode,
  $fanCountries,
  (countryCode, countries) => {
    return (
      (countryCode &&
        countries.reduce<string | null>((result, countryEntity) => {
          if (result) {
            return result
          } else if (countryEntity.code === countryCode) {
            return countryEntity.name
          } else if (countryEntity.states) {
            const state = countryEntity.states.find(
              ({ code }) => code === countryCode,
            )
            const unspecifiedState = state?.code.endsWith('-ZZ')

            return unspecifiedState ? countryEntity.name : (state?.name ?? null)
          }

          return null
        }, null)) ??
      NO_COUNTRY_SELECTED_LABEL
    )
  },
)

sample({
  clock: getCountriesList,
  source: {
    data: $fanCountries,
    isLoading: getCountriesListFx.pending,
  },
  filter: ({ data, isLoading }) => !data.length && !isLoading,
  target: getCountriesListFx,
})

sample({
  clock: updateCountryFx.doneData,
  target: updateFanCountryCode,
})
